import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle, MessageCircle } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import { SEOHead, createBreadcrumbSchema } from '../components/SEOHead';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    schoolName: '',
    subject: 'general',
    message: ''
  });

  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email, 
          phone: formData.phone,
          name: formData.name,
          school: formData.schoolName,
          subject: formData.subject,
          message: formData.message,
          source: `contact_${formData.subject}`
        }),
      });
      
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', schoolName: '', subject: 'general', message: '' });
        setTimeout(() => setStatus(null), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus(null), 3000);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setStatus('error');
      setTimeout(() => setStatus(null), 3000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'info@skolobot.cz',
      link: 'mailto:info@skolobot.cz'
    },
    {
      icon: Phone,
      title: 'Telefon',
      value: '+420 583 033 357',
      link: 'tel:+420583033357'
    },
    {
      icon: MapPin,
      title: 'Adresa',
      value: 'THEBIGDOT s.r.o.',
      detail: 'Zámostní 1155/27, 710 00 Ostrava',
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[color:var(--bg)] to-white">
      <SEOHead 
        title="Kontakt | Školobot"
        description="Kontaktujte tým Školobota. Domluvte si bezplatnou ukázku, webinář nebo získejte cenovou nabídku pro vaši školu. Email: info@skolobot.cz"
        path="/kontakt"
        schema={createBreadcrumbSchema([{ name: 'Úvod', path: '/' }, { name: 'Kontakt', path: '/kontakt' }])}
      />
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h1 className="text-4xl font-bold text-[color:var(--text-primary)] mb-4">
              Kontaktujte nás
            </h1>
            <p className="text-lg text-[color:var(--text-secondary)]">
              Máte otázku nebo potřebujete pomoc? Rádi vám odpovíme.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Contact Info */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <FadeIn key={index} delay={index * 0.1}>
                    {item.link ? (
                      <a href={item.link} className="block bg-white border-2 border-[color:var(--primary-100)] rounded-2xl p-6 hover:border-[color:var(--primary-300)] hover:shadow-lg transition-all">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-[color:var(--primary-100)] rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icon className="w-7 h-7 text-[color:var(--primary-600)]" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-[color:var(--text-secondary)] mb-1">
                              {item.title}
                            </div>
                            <div className="text-lg font-bold text-[color:var(--text-primary)]">
                              {item.value}
                            </div>
                          </div>
                        </div>
                      </a>
                    ) : (
                      <div className="bg-white border-2 border-[color:var(--primary-100)] rounded-2xl p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-[color:var(--primary-100)] rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icon className="w-7 h-7 text-[color:var(--primary-600)]" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-[color:var(--text-secondary)] mb-1">
                              {item.title}
                            </div>
                            <div className="text-lg font-bold text-[color:var(--text-primary)] mb-1">
                              {item.value}
                            </div>
                            {item.detail && (
                              <div className="text-sm text-[color:var(--text-secondary)]">
                                {item.detail}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </FadeIn>
                );
              })}
            </div>

            {/* Right - Form */}
            <FadeIn delay={0.2}>
              <div className="bg-white border-2 border-[color:var(--primary-100)] rounded-3xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-[color:var(--text-primary)] mb-2">
                  Pošlete nám zprávu
                </h2>
                <p className="text-sm text-[color:var(--text-secondary)] mb-6">
                  Vyplňte formulář a ozveme se vám co nejdříve
                </p>

                {status === 'success' ? (
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <h4 className="text-lg font-bold text-green-900 mb-2">Děkujeme!</h4>
                    <p className="text-sm text-green-700">
                      Vaši zprávu jsme obdrželi a brzy se vám ozveme.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                          Jméno a příjmení *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                          placeholder="Jana Nováková"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                          placeholder="jana@skola.cz"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                          Telefon
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                          placeholder="+420 583 033 357"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                          Název školy
                        </label>
                        <input
                          type="text"
                          name="schoolName"
                          value={formData.schoolName}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors"
                          placeholder="ZŠ Praha"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                        Předmět *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors bg-white"
                      >
                        <option value="general">Obecný dotaz</option>
                        <option value="demo">Zájem o prezentaci</option>
                        <option value="school">Nabídka pro školu</option>
                        <option value="pricing">Dotaz k ceníku</option>
                        <option value="support">Technická podpora</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[color:var(--text-primary)] mb-2">
                        Vaše zpráva *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3 border-2 border-[color:var(--primary-100)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)] transition-colors resize-none"
                        placeholder="Napište nám vaši zprávu..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full bg-[color:var(--primary-500)] hover:bg-[color:var(--primary-600)] text-white font-semibold py-4 px-6 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {status === 'sending' ? 'Odesílám...' : (
                        <>
                          <Send className="w-5 h-5" />
                          Odeslat zprávu
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;